import { Injectable, ComponentRef, ViewContainerRef, ApplicationRef, createComponent, EnvironmentInjector } from '@angular/core';
import { CustomToastComponent, ToastData } from '../../components/custom-toast/custom-toast.component';

@Injectable({
  providedIn: 'root'
})
export class CustomToastService {
  private toastComponents: ComponentRef<CustomToastComponent>[] = [];
  private toastContainer: HTMLElement | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {
    this.createToastContainer();
  }

  private createToastContainer() {
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.id = 'custom-toast-container';
      this.toastContainer.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        pointer-events: none;
        z-index: 99999;
      `;
      document.body.appendChild(this.toastContainer);
    }
  }

  showToast(
    message: string, 
    type: 'success' | 'error' | 'warning' | 'info' = 'info',
    duration: number = 3000,
    showCloseButton: boolean = false
  ): string {
    const toastId = this.generateId();
    
    const toastData: ToastData = {
      id: toastId,
      message,
      type,
      duration,
      showCloseButton
    };

    const componentRef = createComponent(CustomToastComponent, {
      environmentInjector: this.injector
    });

    componentRef.instance.toast = toastData;
    componentRef.instance.dismiss.subscribe((id: string) => {
      this.dismissToast(id);
    });

    // Attach to application
    this.appRef.attachView(componentRef.hostView);
    
    // Add to DOM
    if (this.toastContainer) {
      const toastElement = componentRef.location.nativeElement;
      toastElement.style.pointerEvents = 'auto';
      this.toastContainer.appendChild(toastElement);
    }

    this.toastComponents.push(componentRef);

    return toastId;
  }

  dismissToast(toastId: string) {
    const index = this.toastComponents.findIndex(
      comp => comp.instance.toast.id === toastId
    );

    if (index !== -1) {
      const componentRef = this.toastComponents[index];
      
      // Remove from DOM
      const element = componentRef.location.nativeElement;
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }

      // Detach from application
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();

      // Remove from array
      this.toastComponents.splice(index, 1);
    }
  }

  dismissAllToasts() {
    this.toastComponents.forEach(componentRef => {
      const element = componentRef.location.nativeElement;
      if (element && element.parentNode) {
        element.parentNode.removeChild(element);
      }
      this.appRef.detachView(componentRef.hostView);
      componentRef.destroy();
    });
    this.toastComponents = [];
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  // Convenience methods
  success(message: string, duration: number = 3000, showCloseButton: boolean = false): string {
    return this.showToast(message, 'success', duration, showCloseButton);
  }

  error(message: string, duration: number = 4000, showCloseButton: boolean = true): string {
    return this.showToast(message, 'error', duration, showCloseButton);
  }

  warning(message: string, duration: number = 3500, showCloseButton: boolean = false): string {
    return this.showToast(message, 'warning', duration, showCloseButton);
  }

  info(message: string, duration: number = 3000, showCloseButton: boolean = false): string {
    return this.showToast(message, 'info', duration, showCloseButton);
  }
} 